import { useEffect, useRef, useState } from 'react';
import ValidatedInput from '../../validations/ValidatedInput';
import { labelApi } from './api';
import './LabelsScreen.css';

const PRESETS = [
  'linear-gradient(145deg, #3a1058, #8b0068 55%, #d4af37)',
  'linear-gradient(145deg, #2d0a0f, #5c1228 50%, #ff7e5f)',
  'linear-gradient(145deg, #1a0a28, #4b0082 45%, #c44b7a)',
  'linear-gradient(145deg, #0f1a2e, #3a1058 50%, #d4af37)',
  'linear-gradient(145deg, #2a0a18, #8b0068 40%, #e8c96a)',
  'linear-gradient(145deg, #120818, #5c1a7a 55%, #ff7e5f)',
];

function FileDrop({ label, hint, file, preview, onChange }) {
  const inputRef = useRef(null);
  return (
    <button
      type="button"
      className={`col-drop${file || preview ? ' is-filled' : ''}`}
      onClick={() => inputRef.current?.click()}
    >
      <span className="col-drop__label">{label}</span>
      {preview ? (
        <span className="col-drop__preview">
          <img src={preview} alt="" />
        </span>
      ) : (
        <span className="col-drop__icon" aria-hidden>
          ↑
        </span>
      )}
      <span className="col-drop__meta">{file?.name || hint}</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
    </button>
  );
}

export default function LabelsScreen({ onMessage }) {
  const flash = (t) => onMessage?.(t);
  const [labels, setLabels] = useState([]);
  const [labelFile, setLabelFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [labelForm, setLabelForm] = useState({
    name: '',
    description: '',
    gradient: PRESETS[0],
  });

  const refresh = async () => {
    const l = await labelApi.list().catch(() => ({ data: { data: [] } }));
    setLabels(l.data.data || []);
  };

  useEffect(() => {
    refresh().catch(() => flash('Could not load collections'));
  }, []);

  useEffect(() => {
    if (labelFile) {
      const url = URL.createObjectURL(labelFile);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    if (editingId) {
      const item = labels.find((l) => l._id === editingId);
      setPreview(item?.image || '');
      return undefined;
    }
    setPreview('');
    return undefined;
  }, [labelFile, editingId, labels]);

  const resetForm = () => {
    setLabelForm({ name: '', description: '', gradient: PRESETS[0] });
    setLabelFile(null);
    setEditingId(null);
  };

  const saveLabel = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(labelForm).forEach(([k, v]) => fd.append(k, v));
      if (labelFile) fd.append('image', labelFile);
      if (editingId) {
        await labelApi.update(editingId, fd);
        flash('Collection updated');
      } else {
        await labelApi.create(fd);
        flash('Collection saved');
      }
      resetForm();
      await refresh();
    } catch (err) {
      flash(err.response?.data?.message || 'Collection failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (l) => {
    setEditingId(l._id);
    setLabelForm({
      name: l.name || '',
      description: l.description || '',
      gradient: l.gradient || PRESETS[0],
    });
    setLabelFile(null);
  };

  const removeLabel = async (id) => {
    try {
      await labelApi.remove(id);
      flash('Collection removed');
      await refresh();
    } catch (err) {
      flash(err.response?.data?.message || 'Remove failed');
    }
  };

  return (
    <div className="col-screen">
      <form className="col-screen__panel" onSubmit={saveLabel}>
        <p className="col-screen__label">{editingId ? 'Edit collection' : 'New collection'}</p>

        <label className="col-field">
          <span>Name</span>
          <ValidatedInput
            kind="text"
            name="name"
            value={labelForm.name}
            onChange={(e) => setLabelForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Festive Edit"
            required
          />
        </label>

        <label className="col-field">
          <span>Description</span>
          <ValidatedInput
            kind="textarea"
            name="description"
            rows={3}
            value={labelForm.description}
            onChange={(e) => setLabelForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Short line shown on the card"
          />
        </label>

        <div className="col-field">
          <span>Gradient</span>
          <div className="col-presets">
            {PRESETS.map((g) => (
              <button
                key={g}
                type="button"
                className={`col-preset${labelForm.gradient === g ? ' is-active' : ''}`}
                style={{ background: g }}
                aria-label="Gradient preset"
                onClick={() => setLabelForm((f) => ({ ...f, gradient: g }))}
              />
            ))}
          </div>
        </div>

        <FileDrop
          label="Card image"
          hint="Optional overlay photo"
          file={labelFile}
          preview={preview}
          onChange={setLabelFile}
        />

        <div className="col-screen__actions">
          {editingId && (
            <button type="button" className="col-screen__cancel" onClick={resetForm}>Cancel</button>
          )}
          <button type="submit" className="col-screen__save" disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Update collection' : 'Add collection'}
          </button>
        </div>
      </form>

      <aside className="col-screen__aside">
        <div className="col-preview-block">
          <p className="col-screen__label">Live card preview</p>
          <div className="col-preview" style={{ background: labelForm.gradient }}>
            {preview && (
              <div className="col-preview__img" style={{ backgroundImage: `url(${preview})` }} />
            )}
            <div className="col-preview__copy">
              <strong>{labelForm.name || 'Collection name'}</strong>
              <span>{labelForm.description || 'Description appears here'}</span>
            </div>
          </div>
        </div>

        <div className="col-list-block">
          <div className="col-list-block__head">
            <p className="col-screen__label">On landing page</p>
            <span>{labels.length}</span>
          </div>
          <div className="col-list">
            {labels.length === 0 && (
              <p className="col-list__empty">No collections yet — they appear under “Curated Worlds”.</p>
            )}
            {labels.map((l) => (
              <article key={l._id} className={`col-card${editingId === l._id ? ' is-editing' : ''}`} style={{ background: l.gradient || PRESETS[0] }}>
                {l.image && (
                  <div className="col-card__img" style={{ backgroundImage: `url(${l.image})` }} />
                )}
                <div className="col-card__body">
                  <strong>{l.name}</strong>
                  <span>{l.description || 'No description'}</span>
                </div>
                <div className="col-card__actions">
                  <button type="button" className="col-card__edit" onClick={() => startEdit(l)}>Edit</button>
                  <button type="button" className="col-card__remove" onClick={() => removeLabel(l._id)}>Remove</button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}
